import { useEffect, useId, useRef, useState } from 'react';
import {
  searchSignupLocations,
  type SignupLocationKind,
  type SignupLocationRow,
} from '../../../services/signupLocations';
import {
  AUTH_GREEN,
  AUTH_GREEN_SOFT,
  AUTH_INK,
  AUTH_LABEL,
  AUTH_LINE,
  AUTH_MUTED,
  AUTH_SURFACE,
} from './authTheme';

export type LguMonitoringLevel = 'province' | 'municipality' | 'barangay';

type Props = {
  kind: SignupLocationKind;
  label: string;
  selected: SignupLocationRow | null;
  onSelect: (row: SignupLocationRow | null) => void;
  /** Optional parent filter: province_id for municipalities, city_id for barangays */
  parentFilter?: { province_id?: string; city_id?: string };
  disabled?: boolean;
  placeholder?: string;
};

function rowKey(row: SignupLocationRow): string {
  return String(row.id);
}

function rowLabel(row: SignupLocationRow): string {
  const name = row.name?.trim() || rowKey(row);
  if (row.city_id && row.province_id) {
    return `${name} · city ${row.city_id}`;
  }
  if (row.province_id && !row.city_id) {
    return name;
  }
  return name;
}

export default function SignupLocationSearch({
  kind,
  label,
  selected,
  onSelect,
  parentFilter,
  disabled,
  placeholder = 'Search by name…',
}: Props) {
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<SignupLocationRow[]>([]);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    // Clear selection when parent filter changes (carbon cascade).
    if (selected && parentFilter) {
      if (kind === 'municipalities' && parentFilter.province_id) {
        if (selected.province_id && selected.province_id !== parentFilter.province_id) {
          onSelect(null);
        }
      }
      if (kind === 'barangays' && parentFilter.city_id) {
        if (selected.city_id && selected.city_id !== parentFilter.city_id) {
          onSelect(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parent id changes
  }, [parentFilter?.province_id, parentFilter?.city_id, kind]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!open && !q) return;

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError('');
      void searchSignupLocations(kind, {
        search: q || undefined,
        per_page: 25,
        province_id: parentFilter?.province_id,
        city_id: parentFilter?.city_id,
      })
        .then((rows) => setResults(rows))
        .catch((err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            'Could not load locations.';
          setError(typeof msg === 'string' ? msg : 'Could not load locations.');
          setResults([]);
        })
        .finally(() => setLoading(false));
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, kind, open, parentFilter?.province_id, parentFilter?.city_id]);

  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium" style={{ color: AUTH_LABEL }}>
        {label}
      </label>
      {selected ? (
        <div
          className="flex items-center gap-2 rounded-xl border px-3.5 py-3 shadow-sm"
          style={{ borderColor: AUTH_LINE, background: AUTH_SURFACE }}
        >
          <i className="ri-map-pin-line text-xl leading-none" style={{ color: AUTH_MUTED }} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-sm" style={{ color: AUTH_INK }}>
            {rowLabel(selected)}
          </span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              onSelect(null);
              setQuery('');
              setOpen(true);
            }}
            className="shrink-0 text-sm font-semibold hover:underline disabled:opacity-50"
            style={{ color: AUTH_GREEN }}
          >
            Change
          </button>
        </div>
      ) : (
        <div className="relative">
          <i
            className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl leading-none"
            style={{ color: AUTH_MUTED }}
            aria-hidden
          />
          <input
            id={inputId}
            type="search"
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={(e) => {
              setOpen(true);
              e.currentTarget.style.borderColor = AUTH_GREEN;
              e.currentTarget.style.boxShadow = `0 0 0 2px ${AUTH_GREEN_SOFT}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = AUTH_LINE;
              e.currentTarget.style.boxShadow = 'none';
            }}
            className="auth-field w-full rounded-xl border py-3 pl-10 pr-3.5 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 disabled:opacity-50"
            style={{ borderColor: AUTH_LINE, background: AUTH_SURFACE, color: AUTH_INK }}
          />
        </div>
      )}
      {error ? <p className="mt-1.5 text-xs font-medium text-red-500">{error}</p> : null}
      {open && !selected ? (
        <ul
          className="absolute z-20 mt-1.5 max-h-48 w-full overflow-auto rounded-xl border py-1 shadow-lg"
          style={{ borderColor: AUTH_LINE, background: AUTH_SURFACE }}
          role="listbox"
        >
          {loading ? (
            <li className="px-3.5 py-2.5 text-sm" style={{ color: AUTH_MUTED }}>
              Searching…
            </li>
          ) : results.length === 0 ? (
            <li className="px-3.5 py-2.5 text-sm" style={{ color: AUTH_MUTED }}>
              {query.trim() ? 'No matches' : 'Type to search, or wait for first page…'}
            </li>
          ) : (
            results.map((row) => (
              <li key={rowKey(row)}>
                <button
                  type="button"
                  role="option"
                  className="w-full px-3.5 py-2.5 text-left text-sm hover:bg-[#00854014]"
                  style={{ color: AUTH_INK }}
                  onClick={() => {
                    onSelect(row);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  {rowLabel(row)}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
